import { useEffect } from 'react';
import { isServer, logger } from '../lib/utils';

declare global {
  interface Window {
    tarteaucitron: any;
    tarteaucitronForceLanguage: string;
    tarteaucitronForceCDN: string;
    clarity?: (...args: any[]) => void;
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    tac_gtag: (...args: any[]) => void;
  }
}

const TrackingScripts = () => {
  useEffect(() => {
    if (isServer()) return;
    if (document.getElementById('tarteaucitronAlertBig') || document.querySelector('script[src*="tarteaucitron"]')) return;

    // Set global vars BEFORE loading tarteaucitron (it reads them at parse time)
    const initScript = document.createElement('script');
    initScript.textContent = `
      var tarteaucitronForceLanguage = 'de';
      var tarteaucitronForceCDN = '/tarteaucitron/';
      var tarteaucitronCustomText = ${JSON.stringify({
        alertBigPrivacy: 'Wir verwenden Cookies, um Ihre Nutzererfahrung zu verbessern und Inhalte zu personalisieren. Einige Cookies sind unerl\u00e4sslich, w\u00e4hrend andere uns helfen zu verstehen, wie Sie mit unserer Website interagieren. Sie k\u00f6nnen Ihre Einstellungen jederzeit verwalten.',
        personalize: 'Cookie-Einstellungen',
        acceptAll: 'Alle akzeptieren',
      })};
    `;
    document.head.appendChild(initScript);

    // Load tarteaucitron CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/tarteaucitron/tarteaucitron.min.css';
    document.head.appendChild(link);

    // Load custom CSS overrides
    const customLink = document.createElement('link');
    customLink.rel = 'stylesheet';
    customLink.href = '/tarteaucitron/tarteaucitron-custom.css';
    document.head.appendChild(customLink);

    // Load tarteaucitron script
    const script = document.createElement('script');
    script.src = '/tarteaucitron/tarteaucitron.min.js';
    script.onload = () => {
      initTarteaucitron();
    };
    script.onerror = () => {
      logger.error('Failed to load tarteaucitron script');
    };
    document.head.appendChild(script);
  }, []);

  return null;
};

function initTarteaucitron(): void {
  const tac = window.tarteaucitron;
  if (!tac) return;

  // Register custom HubSpot EU service (built-in uses js.hs-scripts.com, we need EU endpoint)
  tac.services.hubspoteu = {
    key: 'hubspoteu',
    type: 'analytic',
    name: 'HubSpot',
    uri: 'https://legal.hubspot.com/privacy-policy',
    needConsent: true,
    cookies: ['__hssc', '__hssrc', '__hstc', 'hubspotutk', '_hs_cookie_cat_pref'],
    js: function () {
      'use strict';
      tac.addScript('https://js-eu1.hs-scripts.com/144975646.js', 'hs-script-loader');
    },
    fallback: function () {},
  };

  // Set user config for built-in services
  tac.user.gtagUa = 'AW-16512706959';
  tac.user.googleadsId = 'AW-16512706959';
  tac.user.clarity = 'qq1hytwkuh';

  // Initialize tarteaucitron
  tac.init({
    privacyUrl: '/datenschutz',
    bodyPosition: 'bottom',
    hashtag: '#datenschutz',
    cookieName: 'tarteaucitron',
    orientation: 'middle',
    showAlertSmall: false,
    cookieslist: false,
    closePopup: false,
    showIcon: false,
    iconPosition: 'BottomRight',
    adblocker: false,
    DenyAllCta: false,
    AcceptAllCta: true,
    highPrivacy: true,
    handleBrowserDNTRequest: false,
    removeCredit: true,
    moreInfoLink: false,
    useExternalCss: true,
    useExternalJs: false,
    mandatory: false,
    mandatoryCta: true,
    googleConsentMode: true,
    bingConsentMode: false,
  });

  // Register services
  (tac.job = tac.job || []).push('gtag');
  (tac.job = tac.job || []).push('googleads');
  (tac.job = tac.job || []).push('clarity');
  (tac.job = tac.job || []).push('hubspoteu');

  logger.log('Tarteaucitron initialized with gtag, Google Ads, Clarity, and HubSpot');
}

// Utility functions for interacting with Clarity
function hasClarityInitialized(): boolean {
  return typeof window !== 'undefined' && typeof window.clarity === 'function';
}

export function identifyUser(customerId: string, sessionId?: string, pageId?: string, friendlyName?: string): void {
  if (isServer() || !hasClarityInitialized()) return;
  window.clarity?.('identify', customerId, sessionId || '', pageId || '', friendlyName || '');
}

export function trackClarityEvent(eventName: string): void {
  if (isServer() || !hasClarityInitialized()) return;
  window.clarity?.('event', eventName);
}

export function setClarityTag(key: string, value: string | string[]): void {
  if (isServer() || !hasClarityInitialized()) return;
  window.clarity?.('set', key, value);
}

export function setClarityCookieConsent(hasConsent = true): void {
  if (isServer() || !hasClarityInitialized()) return;
  window.clarity?.('consent', hasConsent);
}

export default TrackingScripts;
